import { useEffect, useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";
import useTagInput from "./hooks/useTagInput";
import { Tags } from "../../../shared/models/tags.model";
import { TagGroup } from "../../../shared/models/tagGroup.model";

interface iTag {
    tags: Tags[];
    templateId: number;
    tagGroup: TagGroup; 
    tagsLinked: TagGroup[];
    addTag: (tag: Tags, tagGroupId: number) => void;
}

// Helper to add a tag to tagsAdded structure
function addTagToTagsAdded(tagsAdded: any[], tagGroup: any, tag: any) {
    const groupIndex = tagsAdded.findIndex(
        (item) => item.id === tagGroup.id
    );
    if (groupIndex === -1) {
        // Group not found, add new group with this tag
        return [
            ...tagsAdded,
            {
                id: tagGroup.id,
                name: tagGroup.name,
                color: tagGroup.color,
                tags: [tag],
            },
        ];
    } else {
        // Group found, add tag if not already present
        const group = tagsAdded[groupIndex];
        if (!group.tags.some((t: any) => t.id === tag.id && t.name === tag.name)) {
            const updatedGroup = {
                ...group,
                tags: [...group.tags, tag],
            };
            return [
                ...tagsAdded.slice(0, groupIndex),
                updatedGroup,
                ...tagsAdded.slice(groupIndex + 1),
            ];
        }
        // Tag already present, return unchanged
        return tagsAdded;
    }
}

export const TagInput = ({ tags, addTag, tagGroup, tagsLinked,templateId}: iTag) => {
    // track the use input
    const { setSelectedTags, selectedTags, handleRemoveTag, handleAddTag } =
        useTagInput();
    const [deletedTags, setDeletedTags] = useState<number[]>([]);
    const [tagsAdded, setTagsAdded] = useState<any[]>([]);
    const prevSelectedTags = useRef<Tags[]>(selectedTags);
    const [isEditable, setIsEditable] = useState(false);
    const [showSave, setShowSave] = useState(false);
    // Store the initial state when entering edit mode
    const [initialState, setInitialState] = useState<{
        selectedTags: Tags[];
        deletedTags: number[];
        tagsAdded: any[];
    } | null>(null);

    useEffect(() => {
        tagsLinked.forEach((tagLinked) => {
            if (tagLinked.id == tagGroup.id) {
                const linkedTagIds = tagLinked.tags.map((t) => Number(t.id));
                const matchingTags = tags.filter((tag) =>
                    linkedTagIds.includes(tag.id)
                );
                if (matchingTags.length > 0) {
                    setSelectedTags([...selectedTags, ...matchingTags]);
                }
            }
        });

        return () => {
            setSelectedTags([]);
          };
    }, []);

    useEffect(() => {
        prevSelectedTags.current = selectedTags;
    }, [selectedTags]);

    const handleDoubleClick = () => {
        // Store the current state before entering edit mode
        setInitialState({
            selectedTags: [...selectedTags],
            deletedTags: [...deletedTags],
            tagsAdded: [...tagsAdded],
        });
        setIsEditable(true);
        setShowSave(true);
    };

    const handleSave = () => {
        setIsEditable(false);
        setShowSave(false);
        //remove deleted tags from selectedTags
        if (deletedTags.length > 0) handleRemoveTag(templateId, deletedTags);
        //save the new tags linked to template
        if (tagsAdded.length > 0) {
            handleAddTag(templateId, tagsAdded);
        }
        // Clear the initial state after successful save
        setInitialState(null);
    };

    const handleCancel = () => {
        // Restore the initial state when canceling
        if (initialState) {
            setSelectedTags(initialState.selectedTags);
            setDeletedTags(initialState.deletedTags);
            setTagsAdded(initialState.tagsAdded);
        }
        setIsEditable(false);
        setShowSave(false);
        setInitialState(null);
    };

    return (
        <div
            className="position-relative w-full"
            
            onDoubleClick={handleDoubleClick}
        >
            {showSave && (
                <div
                    className="position-absolute end-0 d-flex items-center"
                    style={{ top: "-27px" }}
                >
                    <button
                        style={{ width: "20px", height: "20px" }}
                        className=" end-0 btn-outline-success btn p-0 d-flex align-items-center justify-content-center"
                        onClick={handleSave}
                        type="button"
                    >
                        <i className="bi bi-check fs-16"></i>
                    </button>
                    <button
                        style={{ width: "20px", height: "20px" }}
                        className="btn-outline-danger btn p-0 m-0 ms-2 d-flex align-items-center justify-content-center"
                        onClick={handleCancel}
                        type="button"
                    >
                        <i className="bi bi-x fs-16"></i>
                    </button>
                </div>
            )}
            <CreatableSelect
                isMulti
                name="states[]"
                options={tags.map((tag) => ({
                    value: tag.id,
                    label: tag.name,
                }))}
                value={selectedTags.map((tag) => ({
                    value: tag.id,
                    label: tag.name,
                }))}
                isDisabled={!isEditable}
                styles={{
                    control: (base, _state) => ({
                        ...base,
                        backgroundColor: !isEditable
                            ? "#f1f1f1"
                            : base.backgroundColor,
                        opacity: !isEditable ? 0.7 : 1,
                        cursor: !isEditable ? "not-allowed" : "default",
                    }),
                    input: (base) => ({
                        ...base,
                        color: !isEditable ? "#888" : base.color,
                    }),
                    multiValue: (base) => ({
                        ...base,
                        borderRadius: "10px", // Increased border radius for selected options
                    }),
                    multiValueLabel: (base) => ({
                        ...base,
                        borderRadius: "10px", // Match the container border radius
                    }),
                    multiValueRemove: (base) => ({
                        ...base,
                        borderRadius: "10px", // Match the container border radius
                    }),
                }}
                onChange={(newValue) => {
                    const typedValue = newValue as {
                        value: number;
                        label: string;
                    }[];
                    const mappedTags = typedValue.map((v) => ({
                        id: v.value,
                        name: v.label,
                        tagGroupId: tagGroup.id,
                    }));

                    // Detect removed tag
                    const removed = prevSelectedTags.current.find(
                        (prev) => !mappedTags.some((tag) => tag.id === prev.id)
                    );
                    if (removed) {
                        
                        setSelectedTags(mappedTags);
                        setDeletedTags([...deletedTags, removed.id]);
                        // check if the tag is in the ones that are added to take it out of the added tags
                        if (tagsAdded.some((tag) => tag.id === removed.id)) {
                            setTagsAdded(
                                tagsAdded.filter((tag) => tag.id !== removed.id)
                            );
                        }
                    }
                    //removed from setSelectedTags local the deleted ones

                    // Detect added tag
                    const added = mappedTags.find(
                        (tag) =>
                            !prevSelectedTags.current.some(
                                (prev) => prev.id === tag.id
                            )
                    );
                    if (added) {
                        setTagsAdded(
                            addTagToTagsAdded(tagsAdded, tagGroup, added)
                        );
                        //check if the tag is in the ones that are deleted to take it out of the deleted tags
                        if (deletedTags.includes(added.id)) {
                            setDeletedTags(
                                deletedTags.filter((tag) => tag !== added.id)
                            );
                        }
                        setSelectedTags(mappedTags);
                    }
                }}
                className={`basic-multi-select mb-4 ${isEditable ? "select-editable" : ""}`}
                isSearchable
                menuPlacement="auto"
                classNamePrefix="Select2"
                placeholder={(selectedTags.length == 0 && !isEditable) ? "double click to start adding tags" : ""}
                components={{
                    DropdownIndicator: () => null,
                }}
                onCreateOption={(inputValue: string) => {
                    
                    const newOption = {
                        id: -1,
                        name: inputValue,
                        tagGroupId: Number(tagGroup.id),
                        color: tagGroup.color || null,
                        tagGroup: tagGroup,
                    };
                    
                    addTag(newOption, tagGroup.id);
                    //handleAddTag(templateId, inputValue, tagGroupId);
                    setSelectedTags([...selectedTags, newOption]);
                    const tagToAdd = newOption.id == -1
                            ? {
                                  name: newOption.name,
                                  tagGroupId: newOption.tagGroupId,
                              }
                            : newOption;

                    const newTags = addTagToTagsAdded(
                        tagsAdded,
                        { id: tagGroup.id, name: tagGroup.name },
                        tagToAdd
                    );
                    
                    setTagsAdded(newTags);
                    
                }}
                formatCreateLabel={(inputValue: string) =>
                    `Create "${inputValue}"`
                }
            />
        </div>
    );
};
